import React, { useCallback, useEffect, useState } from 'react'

import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { useApi } from '@/hooks'
import { buildQuery } from '@/insights/common/queryUtils'
import { SAMPLE_LIMIT } from '@/insights/fuel/reeferFuelGraph/constants'
import ReeferFuelChart from '@/insights/fuel/reeferFuelGraph/ReeferFuelChart'
import type { ReeferFuelSample } from '@/insights/fuel/reeferFuelGraph/types'
import { toWallClock } from '@/insights/fuel/reeferFuelGraph/window'

import GraphPanelShell from './GraphPanelShell'

import type { GraphPanelProps } from '../types'

const PATH = '/insights/fuel/reefer-fuel-by-asset'
const CHART_PERIOD_HOURS = 24

/** Reefer fuel telemetry for the acknowledged alert's asset, shown in place of the
 *  temperature graph when the alert is a fuel alert (MEGA: Fuel Alert Acknowledgement).
 *
 *  Note this endpoint's window is NOT the same shape as `/assets/history`: it takes an
 *  explicit `to` rather than `periodHours`, and both bounds as zone-less wall clock. The
 *  formatting comes from the Reefer Fuel Graph insight's own `toWallClock`, so the two
 *  callers cannot drift on the part that matters — an offset-bearing ISO string here would
 *  shift the window by the user's UTC offset and quietly return the wrong data. */
const FuelGraphPanel: React.FC<GraphPanelProps> = ({ assetId, companyId, chartFrom, alertMarkers, className }) => {
  const { api } = useApi()

  const [data, setData] = useState<ReeferFuelSample[]>()
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const fetchWindow = useCallback(
    async (window: HistoryWindow): Promise<ReeferFuelSample[] | undefined> => {
      const query = buildQuery({
        companyId,
        assetIds: String(assetId),
        date: 'custom',
        from: toWallClock(window.from),
        to: toWallClock(window.to),
      })

      const response = await api.get<ReeferFuelSample[]>({
        path: `${PATH}?${query}&skip=0&take=${SAMPLE_LIMIT}`,
        origin: 'FuelGraphPanel#fetchWindow',
      })

      if (response.error) {
        throw new Error(response.message || 'Unable to load fuel graph data.')
      }

      return response.data ?? undefined
    },
    [api, assetId, companyId]
  )

  const reload = useCallback(
    // IMPORTANT: don't push the result back into `data` — the chart owns its window
    // internally, and changing the `data` prop here would make it treat the fetch as a
    // reconfigure and discard the user's range.
    async (_: unknown, window: HistoryWindow): Promise<ReeferFuelSample[] | undefined> => {
      try {
        return await fetchWindow(window)
      } catch {
        return undefined
      }
    },
    [fetchWindow]
  )

  useEffect(() => {
    let isActive = true

    const load = async () => {
      try {
        setIsLoading(true)
        setError('')
        const response = await fetchWindow({
          from: chartFrom,
          to: chartFrom.plus({ hours: CHART_PERIOD_HOURS }),
        })
        if (isActive) setData(response)
      } catch {
        if (isActive) {
          setData(undefined)
          setError('Unable to load fuel graph data.')
        }
      } finally {
        if (isActive) setIsLoading(false)
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [fetchWindow, chartFrom])

  return (
    <GraphPanelShell
      title="Fuel Graph"
      className={className}
      error={error}
      isLoading={isLoading}
      // The endpoint returns an empty array (not undefined) for a window with no
      // samples, so a bare truthiness check would render the chart instead of the empty state.
      hasData={!!data?.length}
      emptyMessage="No fuel data found for the selected alert window."
    >
      {(chartHeight) => (
        <ReeferFuelChart
          data={data as ReeferFuelSample[]}
          from={chartFrom}
          periodHours={CHART_PERIOD_HOURS}
          alertMarkers={alertMarkers}
          reload={reload}
          chartHeight={chartHeight}
        />
      )}
    </GraphPanelShell>
  )
}

export default FuelGraphPanel
